import React, { useCallback } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import AddRecordsWizard from "@app/scene/map/components/LeadFinderMode/components/AddRecordsWizard";
import ResultsModal from "@app/scene/map/components/LeadFinderMode/components/ResultsModal";
import { setAddRecordsVisibility } from "@app/scene/map/store/actions";
import {
  getFilteredLeadFinderRecordsCount,
  getFilteredSelectedRecordsCount,
  isAddRecordsVisible,
  isResultModalVisible,
} from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./Content.module.scss";

const messages = defineMessages({
  add: {
    id: "map.tool.leadFinder.sidebar.add",
    defaultMessage: "Add to Records",
    description: "Lead Finder sidebar - Add to Records button title",
  },
  empty: {
    id: "map.tool.leadFinder.sidebar.emptySelection",
    defaultMessage: "No leads selected yet",
    description: "Lead Finder sidebar title - no selection",
  },
  selected: {
    id: "map.tool.leadFinder.sidebar.selected",
    defaultMessage:
      "{selectedRecordsCount}/{total} {selectedRecordsCount, plural, one {Lead} other {Leads}} Selected",
    description: "Lead Finder sidebar title - number of selected records",
  },
});

interface Props {
  addRecordsVisible: boolean;
  resultsVisible: boolean;
  selectedRecordsCount: number;
  setAddRecordsVisibility: typeof setAddRecordsVisibility;
  totalRecords: number;
}

const Content: React.FC<Props> = ({
  addRecordsVisible,
  resultsVisible,
  selectedRecordsCount,
  setAddRecordsVisibility,
  totalRecords,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const hasSelection = selectedRecordsCount > 0;

  const handleAddToRecordsClick = useCallback(() => {
    if (hasSelection) {
      setAddRecordsVisibility({ visible: true });
      analytics.clicked(["Add to records"]);
    }
  }, [analytics, hasSelection, setAddRecordsVisibility]);

  return (
    <div className={styles.container}>
      <div className={styles.selection}>
        <div className={styles.selectionCount}>
          {hasSelection
            ? intl.formatMessage(messages.selected, { selectedRecordsCount, total: totalRecords })
            : intl.formatMessage(messages.empty)}
        </div>
        <Button disabled={!hasSelection} onClick={handleAddToRecordsClick} type="primary">
          {intl.formatMessage(messages.add)}
        </Button>
        {hasSelection && addRecordsVisible && <AddRecordsWizard />}
        {resultsVisible && <ResultsModal />}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  addRecordsVisible: isAddRecordsVisible(state),
  resultsVisible: isResultModalVisible(state),
  selectedRecordsCount: getFilteredSelectedRecordsCount(state),
  totalRecords: getFilteredLeadFinderRecordsCount(state),
});

const mapDispatchToProps = {
  setAddRecordsVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(Content);
